// SHADOW NETWORK — Single analysis card.
// Used inside ShadowFeed below. Renders the post image, badges,
// title/desc, author, reaction toolbar, comment count, view count.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Flame, TrendingUp, TrendingDown, Brain,
  MessageSquare, Eye, Crown, Trash2, ShieldCheck, ZoomIn,
  Check, X as XIcon, Hourglass, Repeat, RefreshCw,
} from "lucide-react";
import { API, safeJson } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLightbox } from "./ImageLightbox";
import { AnalysisComments } from "./AnalysisComments";
import { SharePost } from "./SharePost";
import { VideoCard } from "./VideoCard";
import { AnalysisSocialBar } from "./shadow/AnalysisSocialBar";
import { renderWithMentions } from "./shadow/renderWithMentions";
import { TipButton } from "./shadow/TipButton";
import { tFases } from "../i18n/shadow-fases";
import { Link as RouterLink } from "react-router-dom";

const PremiumLock = ({ postId, authorUsername, lang }) => (
  <span className="inline-flex flex-col items-start gap-1.5 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] p-2.5 text-xs text-amber-100">
    <span className="inline-flex items-center gap-1.5 font-bold uppercase tracking-wider">
      🔒 {tFases(lang, "premium_lock_title")}
    </span>
    <span className="text-[11px] text-white/55">{tFases(lang, "premium_lock_sub", { u: authorUsername || "..." })}</span>
    {authorUsername && (
      <RouterLink to={`/shadow/perfil/${authorUsername}`} data-testid={`signal-subscribe-cta-${postId}`} className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-amber-400 px-3 py-1 text-[10px] font-black uppercase tracking-wider text-black hover:-translate-y-0.5 transition-all">
        {tFases(lang, "premium_lock_cta")}
      </RouterLink>
    )}
  </span>
);

// FASE 3.5 (3.3) — Co-author badge rendered next to the author name.
const CoAuthorBadge = ({ postId, coAuthors, meta }) => {
  // Take the first accepted co-author (v1 supports a single co-author).
  const firstId = coAuthors[0];
  const info = (meta && meta[firstId]) || {};
  const uname = info.username;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-cyan-400/40 bg-cyan-400/[0.08] px-1.5 py-px text-[8px] font-black uppercase tracking-widest text-cyan-200"
      data-testid={`card-coauthor-${postId}`}
      title={uname ? `+ @${uname}` : "co-signed"}
    >
      🤝 {uname ? `+ @${uname}` : "co"}
    </span>
  );
};
import { useLanguage } from "../i18n/LanguageContext";
import { getShadowI18n } from "../i18n/shadow";

const DIRECTION_STYLES = {
  LONG: { bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.35)", color: "#34d399", label: "LONG ↑" },
  SHORT: { bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.35)", color: "#f87171", label: "SHORT ↓" },
  NEUTRAL: { bg: "rgba(107,114,128,0.18)", border: "rgba(255,255,255,0.12)", color: "#9ca3af", label: "NEUTRAL" },
};

const REACTIONS = [
  { id: "FIRE", icon: Flame, color: "#f97316" },
  { id: "BULLISH", icon: TrendingUp, color: "#10b981" },
  { id: "BEARISH", icon: TrendingDown, color: "#ef4444" },
  { id: "SMART", icon: Brain, color: "#a855f7" },
];

// Locale-aware "time ago" — caller passes the localized strings so we don't
// depend on a hook here (this fn lives outside React).
const timeAgo = (iso, ui) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return ui.ac_ago_now;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString();
};

// SHADOW RANKING — score badge (extracted to avoid IIFE inside JSX,
// which trips the react-hooks/purity rule).
const ScoreBadge = ({ post, score, pendingLabel }) => {
  if (!post.direction) return null;
  const ageHours = post.created_at ? (Date.now() - new Date(post.created_at).getTime()) / 3_600_000 : 0;
  if (!score && ageHours >= 24) return null;
  if (!score) {
    return (
      <span
        className="pointer-events-none absolute left-3 bottom-3 inline-flex items-center gap-1 rounded border border-white/15 bg-black/65 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-white/55 backdrop-blur-sm"
        data-testid={`shadow-card-score-pending-${post.id}`}
        title={pendingLabel}
      >
        <Hourglass className="h-2.5 w-2.5" />
      </span>
    );
  }
  const ok = score.direction_correct === true;
  const ko = score.direction_correct === false;
  const color = ok ? "#00e676" : ko ? "#ff3355" : "#94a3b8";
  const Icon = ok ? Check : ko ? XIcon : Hourglass;
  return (
    <span
      className="pointer-events-none absolute left-3 bottom-3 inline-flex items-center gap-1 rounded border bg-black/65 px-1.5 py-0.5 font-mono text-[10px] font-black tracking-[0.12em] backdrop-blur-sm"
      style={{ borderColor: `${color}66`, color }}
      data-testid={`shadow-card-score-${post.id}`}
      title={score.verdict || ""}
    >
      <Icon className="h-2.5 w-2.5" /> {Math.round(score.total_score || 0)}
    </span>
  );
};

export const AnalysisCard = ({ analysis, onDeleted, onUpdated }) => {
  const { user, openLogin } = useAuth();
  const { lang } = useLanguage();
  const ui = getShadowI18n(lang);
  const lightbox = useLightbox();
  const [post, setPost] = useState(analysis);
  const [bumpId, setBumpId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(analysis.comment_count || 0);
  const [reposting, setReposting] = useState(false);
  const [reposted, setReposted] = useState(false);
  const [score, setScore] = useState(null);
  // Feed description: show ~8-10 lines / 500 chars by default, expand on click.
  // On post detail page (/shadow/post/*) start expanded.
  const _isDetailPage =
    typeof window !== "undefined" &&
    /\/shadow\/post\//.test(window.location.pathname);
  const [descExpanded, setDescExpanded] = useState(_isDetailPage);
  const _descNeedsToggle =
    !!post.description &&
    !(post.premium && user?.id !== post.user_id) &&
    (post.description.length > 500 || (post.description.match(/\n/g) || []).length > 8);
  const _moreLabel = lang === "en" ? "View more" : lang === "sk" ? "Zobraziť viac" : "Ver más";
  const _lessLabel = lang === "en" ? "View less" : lang === "sk" ? "Zobraziť menej" : "Ver menos";

  const isOwner = user && user.id === post.user_id;
  const isAdmin = !!user?.is_admin;
  const canDelete = isOwner || isAdmin;
  const dirStyle = post.direction ? DIRECTION_STYLES[post.direction] : null;

  // SHADOW RANKING — lightweight score fetch (skips silently if not evaluated yet)
  useEffect(() => {
    if (!post?.id || !post?.direction) return;
    const ageHours = post.created_at
      ? (Date.now() - new Date(post.created_at).getTime()) / 3_600_000
      : 0;
    if (ageHours < 1) return; // very recent — no point asking
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/ranking/analysis/${post.id}`);
        const data = await safeJson(res);
        if (!cancelled && res.ok && data?.score) setScore(data.score);
      } catch (_e) { /* non-critical */ }
    })();
    return () => { cancelled = true; };
  }, [post.id, post.direction, post.created_at]);

  const react = async (type) => {
    if (!user) { openLogin(); return; }
    setBumpId(type);
    setTimeout(() => setBumpId(null), 320);
    try {
      const token = localStorage.getItem("rebel_jwt");
      const res = await fetch(`${API}/shadow-posts/${post.id}/react`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ type }),
      });
      const data = await safeJson(res);
      if (res.ok && data) {
        const updated = {
          ...post,
          reaction_count: data.reaction_count,
          reaction_breakdown: data.breakdown,
          viewer_reaction: data.viewer_reaction,
        };
        setPost(updated);
        try { onUpdated?.(updated); } catch (_e) { /* non-critical */ }
      }
    } catch (_e) { /* non-critical */ }
  };

  const handleDelete = async () => {
    if (deleting) return;
    const msg = isOwner
      ? "¿Eliminar este análisis?"
      : "¿Eliminar este análisis como administrador?\nEsta acción no se puede deshacer.";
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem("rebel_jwt");
      const res = await fetch(`${API}/shadow-posts/${post.id}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (res.ok) {
        try { onDeleted?.(post.id); } catch (_e) { /* non-critical */ }
      }
    } finally {
      setDeleting(false);
    }
  };

  const openImage = () => {
    const caption = [post.title, post.ticker, post.timeframe].filter(Boolean).join(" — ");
    lightbox.open(post.image_url, caption);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="group overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/80 backdrop-blur-sm transition-all hover:border-orange-400/40 hover:shadow-[0_18px_50px_rgba(255,140,0,0.12)]"
      data-testid={`shadow-card-${post.id}`}
    >
      {/* IMAGE / VIDEO */}
      <div className="relative aspect-[16/9] overflow-hidden bg-black">
        {post.media_type === "video" && post.video_url ? (
          <VideoCard
            videoUrl={post.video_url}
            thumbnailUrl={post.image_url}
            duration={post.video_duration}
            postId={post.id}
          />
        ) : (
          <button
            type="button"
            onClick={openImage}
            className="block h-full w-full cursor-zoom-in"
            aria-label={ui.ac_zoom_aria}
            data-testid={`shadow-card-image-${post.id}`}
          >
            <img
              src={post.image_url}
              alt={post.title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            />
            <span className="pointer-events-none absolute right-3 bottom-3 inline-flex items-center gap-1 rounded-full border border-white/20 bg-black/55 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-white/80 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
              <ZoomIn className="h-3 w-3" /> {ui.ac_zoom_image}
            </span>
          </button>
        )}
        {/* SHADOW RANKING — score badge (bottom-left of image, never conflicts with delete) */}
        <ScoreBadge post={post} score={score} pendingLabel={ui.ac_eval_pending} />
        {/* Badges */}
        <div className="pointer-events-none absolute left-3 top-3 flex flex-wrap gap-1.5">
          {post.post_type === "diploma" && post.diploma_meta && (
            <Badge color="#f5a623" bg="rgba(245,166,35,0.18)" border="rgba(245,166,35,0.55)">
              🎓 RE₿EL PROOF™ · NIVEL {post.diploma_meta.nivel}
            </Badge>
          )}
          {post.ticker && (
            <Badge color="#22d3ee" bg="rgba(34,211,238,0.14)" border="rgba(34,211,238,0.35)">
              {post.ticker}
            </Badge>
          )}
          {post.timeframe && (
            <Badge color="#fbbf24" bg="rgba(251,191,36,0.14)" border="rgba(251,191,36,0.35)">
              {post.timeframe}
            </Badge>
          )}
          {dirStyle && (
            <Badge color={dirStyle.color} bg={dirStyle.bg} border={dirStyle.border}>
              {dirStyle.label}
            </Badge>
          )}
        </div>

        {canDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(); }}
            disabled={deleting}
            className={`absolute right-3 top-3 inline-flex items-center gap-1 rounded-full border p-1.5 text-[9px] font-black uppercase tracking-wider backdrop-blur transition-opacity ${
              !isOwner && isAdmin
                ? "border-cyan-400/40 bg-cyan-400/15 text-cyan-200 opacity-100 hover:bg-cyan-400/25"
                : "border-rose-500/40 bg-rose-500/[0.16] text-rose-200 opacity-0 group-hover:opacity-100 hover:bg-rose-500/30"
            }`}
            aria-label={ui.ac_delete}
            title={!isOwner && isAdmin ? ui.ac_delete_admin : ui.ac_delete}
            data-testid={`shadow-card-delete-${post.id}`}
          >
            {!isOwner && isAdmin ? (
              <>
                <ShieldCheck className="h-3 w-3" />
                <span>{ui.ac_admin_label}</span>
                <Trash2 className="h-3 w-3" />
              </>
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
          </button>
        )}
      </div>

      {/* AUTHOR */}
      <div className="flex items-center gap-2 px-4 pt-3">
        {post.user_username ? (
          <Link
            to={`/shadow/perfil/${post.user_username}`}
            onClick={(e) => e.stopPropagation()}
            className="shrink-0"
            aria-label={`Ver perfil de ${post.user_name || post.user_username}`}
            data-testid={`card-author-avatar-${post.id}`}
          >
            <div
              className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-amber-600 text-[11px] font-black text-black ring-0 transition-all hover:ring-2 hover:ring-amber-400/60"
              aria-hidden
            >
              {(post.user_name || "?").trim().charAt(0).toUpperCase()}
            </div>
          </Link>
        ) : (
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-amber-600 text-[11px] font-black text-black"
            aria-hidden
          >
            {(post.user_name || "?").trim().charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {post.user_username ? (
              <Link
                to={`/shadow/perfil/${post.user_username}`}
                onClick={(e) => e.stopPropagation()}
                data-testid={`card-author-name-${post.id}`}
                className="truncate text-xs font-bold text-white transition-colors hover:text-amber-300"
              >
                {post.user_name}
              </Link>
            ) : (
              <span className="truncate text-xs font-bold text-white">{post.user_name}</span>
            )}
            {post.user_is_ambassador && (
              <span title="Ambassador" className="inline-flex items-center text-amber-300">
                <Crown className="h-3 w-3" />
              </span>
            )}
            {post.user_is_pro && (
              <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-1.5 py-px text-[8px] font-black tracking-widest text-amber-200">
                PRO
              </span>
            )}
            {Array.isArray(post.co_authors) && post.co_authors.length > 0 && (
              <CoAuthorBadge postId={post.id} coAuthors={post.co_authors} meta={post.co_authors_meta} />
            )}
            {user && post.user_id && user.id !== post.user_id && (
              <Link
                to={`/shadow/mensajes/${post.user_id}`}
                onClick={(e) => e.stopPropagation()}
                title="Enviar mensaje privado"
                data-testid={`card-message-${post.id}`}
                className="ml-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/[0.06] text-[11px] text-white/55 transition-all hover:border-amber-400/50 hover:bg-white/[0.1] hover:text-amber-300"
              >
                💬
              </Link>
            )}
          </div>
          <span className="text-[10px] text-white/40">{ui.ac_ago_prefix ? `${ui.ac_ago_prefix} ` : ""}{timeAgo(post.created_at, ui)}</span>
        </div>
      </div>

      {/* TITLE + DESC */}
      <div className="px-4 pb-3 pt-2">
        <h3 className="line-clamp-2 text-sm font-black tracking-tight text-white">{post.title}</h3>
        <div className="mt-1.5 pr-1 shadow-feed-desc">
          <p
            className={`whitespace-pre-wrap text-xs leading-relaxed text-white/55 ${
              descExpanded ? "" : "line-clamp-[10]"
            }`}
            data-testid={`shadow-card-desc-${post.id}`}
          >
            {post.premium && user?.id !== post.user_id ? (
              <PremiumLock postId={post.id} authorUsername={post.user_username} lang={lang} />
            ) : (
              renderWithMentions(post.description)
            )}
          </p>
          {_descNeedsToggle && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setDescExpanded((v) => !v);
              }}
              className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.04] px-2 py-0.5 font-mono text-[10px] font-bold text-white/70 transition-colors hover:border-orange-400/40 hover:bg-orange-400/[0.08] hover:text-orange-200"
              data-testid={`shadow-card-desc-toggle-${post.id}`}
              aria-expanded={descExpanded}
              aria-controls={`shadow-card-desc-${post.id}`}
            >
              {descExpanded ? `${_lessLabel} ▲` : `${_moreLabel} ▼`}
            </button>
          )}
        </div>
        {post.poll && (
          <PollCard
            poll={post.poll}
            postId={post.id}
            lang={lang}
            onVoted={(updated) => {
              const next = { ...post, poll: updated };
              setPost(next);
              try { onUpdated?.(next); } catch (_e) { /* non-critical */ }
            }}
          />
        )}
        {post.post_type === "diploma" && post.diploma_meta && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`shadow-card-diploma-cta-${post.id}`}>
            <a
              href={post.diploma_meta.basescan_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 rounded-md border border-cyan-400/30 bg-cyan-400/10 px-2 py-1 text-[10px] font-bold text-cyan-300 transition-all hover:bg-cyan-400/20"
            >
              ⛓ BaseScan
            </a>
            <a
              href={post.diploma_meta.opensea_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 rounded-md border border-fuchsia-400/30 bg-fuchsia-400/10 px-2 py-1 text-[10px] font-bold text-fuchsia-300 transition-all hover:bg-fuchsia-400/20"
            >
              OpenSea
            </a>
            <a
              href={post.diploma_meta.verify_url}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 rounded-md border border-amber-400/40 bg-amber-400/10 px-2 py-1 text-[10px] font-bold text-amber-300 transition-all hover:bg-amber-400/20"
            >
              <ShieldCheck className="h-3 w-3" /> Verificar
            </a>
          </div>
        )}
      </div>

      {/* REACTIONS + META — wraps to two rows on very narrow cards */}
      <div className="flex flex-wrap items-center justify-between gap-y-1.5 gap-x-2 border-t border-white/[0.06] px-3 py-2.5">
        <div className="flex items-center gap-1">
          {REACTIONS.map((r) => {
            const active = post.viewer_reaction === r.id;
            const count = post.reaction_breakdown?.[r.id] || 0;
            return (
              <motion.button
                key={r.id}
                onClick={() => react(r.id)}
                animate={bumpId === r.id ? { scale: [1, 1.35, 1] } : {}}
                transition={{ duration: 0.3 }}
                className="inline-flex items-center gap-0.5 rounded-full border px-1.5 py-1 text-[10px] font-bold transition-colors"
                style={{
                  borderColor: active ? r.color : "rgba(255,255,255,0.08)",
                  color: active ? r.color : "rgba(255,255,255,0.5)",
                  background: active ? `${r.color}1a` : "transparent",
                }}
                data-testid={`shadow-react-${r.id}-${post.id}`}
                aria-label={r.id}
              >
                <r.icon className="h-3 w-3" />
                {count > 0 && <span>{count}</span>}
              </motion.button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-white/40 sm:gap-3">
          <SharePost analysis={post} />
          {user?.is_admin && (
            <button
              type="button"
              onClick={() => {
                const postUrl = `${window.location.origin}/api/og/post/${post.id}?v=${Date.now()}`;
                window.open(
                  `https://cards-dev.twitter.com/validator?url=${encodeURIComponent(postUrl)}`,
                  "_blank",
                  "noopener,noreferrer",
                );
              }}
              title="Refrescar preview OG en X (admin)"
              aria-label="Refrescar preview OG"
              data-testid={`shadow-card-og-refresh-${post.id}`}
              className="inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[#f97316]/70 transition-colors hover:bg-[#f97316]/10 hover:text-[#f97316]"
            >
              <RefreshCw className="h-3 w-3" />
            </button>
          )}
          <button
            onClick={() => setShowComments((s) => !s)}
            className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 transition-colors ${
              showComments ? "bg-cyan-400/10 text-cyan-200" : "hover:text-white"
            }`}
            data-testid={`shadow-card-comments-toggle-${post.id}`}
            aria-expanded={showComments}
          >
            <MessageSquare className="h-3 w-3" /> {commentCount}
          </button>
          {user && post.user_id && user.id !== post.user_id && (
            <TipButton tipAddress={post.user_tip_address} authorName={post.user_name} postId={post.id} />
          )}
          {user && post.user_id && user.id !== post.user_id && post.type !== "repost" && (
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                if (reposting) return;
                setReposting(true);
                try {
                  const res = await fetch(`${API}/shadow-posts/${post.id}/repost`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ comment: "" }),
                  });
                  if (res.ok) {
                    setReposted(true);
                  } else {
                    const j = await res.json().catch(() => ({}));
                    if (j?.detail?.error === "already_reposted") setReposted(true);
                  }
                } finally {
                  setReposting(false);
                }
              }}
              disabled={reposting || reposted}
              className={`inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 transition-colors ${
                reposted ? "text-emerald-300" : "text-white/60 hover:text-emerald-300"
              }`}
              data-testid={`shadow-card-repost-${post.id}`}
              title={reposted ? tFases(lang, "repost_done") : tFases(lang, "repost_action")}
            >
              <Repeat className="h-3 w-3" />
              {reposted ? " ✓" : ""}
            </button>
          )}
          <span className="inline-flex shrink-0 items-center gap-1">
            <Eye className="h-3 w-3" /> {post.view_count || 0}
          </span>
        </div>
      </div>

      {showComments && (
        <AnalysisComments
          postId={post.id}
          onCountChange={(n) => {
            setCommentCount(n);
            const updated = { ...post, comment_count: n };
            setPost(updated);
            try { onUpdated?.(updated); } catch (_e) { /* non-critical */ }
          }}
        />
      )}

      {/* Social bar — additive: like + bookmark (Fase 5 integration) */}
      <AnalysisSocialBar
        analysisId={post.id}
        commentsCount={commentCount}
        onAuthRequired={openLogin}
      />
    </motion.article>
  );
};

const Badge = ({ children, color, bg, border }) => (
  <span
    className="rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.15em] backdrop-blur"
    style={{ color, background: bg, border: `1px solid ${border}` }}
  >
    {children}
  </span>
);

// FASE 1.2 — Poll widget rendered inside an analysis card.
// Renders question + options. Authed users can vote (one vote per user).
// After voting, shows bars with percentages.
// UX FIX: collapsed by default in feed; expanded by default on post detail page
// (/shadow/post/*). Voting logic untouched.
const POLL_LABELS = {
  es: { show: "Ver encuesta", hide: "Ocultar" },
  en: { show: "View poll", hide: "Hide" },
  sk: { show: "Zobraziť anketu", hide: "Skryť" }, de: { show: "Umfrage anzeigen", hide: "Ausblenden" }, "zh-CN": { show: "查看投票", hide: "隐藏" },
};

const PollCard = ({ poll, postId, onVoted, lang }) => {
  const { user, openLogin } = useAuth();
  const [busy, setBusy] = useState(false);
  const isDetailPage =
    typeof window !== "undefined" &&
    /\/shadow\/post\//.test(window.location.pathname);
  const [expanded, setExpanded] = useState(isDetailPage);
  const voted = poll?.viewer_voted;
  const total = poll?.total_votes || 0;
  const options = poll?.options || [];
  const votes = poll?.votes || {};
  const labels = POLL_LABELS[lang] || POLL_LABELS.en || POLL_LABELS.es;

  const cast = async (idx) => {
    if (!user) { openLogin(); return; }
    if (voted || busy) return;
    setBusy(true);
    try {
      const token = localStorage.getItem("rebel_jwt");
      const res = await fetch(`${API}/shadow-posts/${postId}/vote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ option_index: idx }),
      });
      const data = await safeJson(res);
      if (res.ok && data) {
        onVoted?.({
          ...poll,
          votes: data.votes || votes,
          total_votes: data.total_votes ?? total,
          viewer_voted: true,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-3 rounded-xl border border-cyan-400/20 bg-cyan-400/[0.03] p-3"
      data-testid={`shadow-poll-${postId}`}
    >
      <div className="flex items-start gap-2">
        <p className="flex-1 text-[11px] font-bold text-cyan-200">
          📊 {poll.question}
        </p>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={`shadow-poll-body-${postId}`}
          className="shrink-0 inline-flex items-center gap-1 rounded-md border border-cyan-400/30 bg-cyan-400/[0.06] px-2 py-0.5 font-mono text-[10px] font-bold text-cyan-100 transition-colors hover:bg-cyan-400/[0.14]"
          data-testid={`shadow-poll-toggle-${postId}`}
        >
          {expanded ? `${labels.hide} ▲` : `${labels.show} ▼`}
        </button>
      </div>

      <div
        id={`shadow-poll-body-${postId}`}
        className={`grid transition-all duration-300 ease-out ${
          expanded ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="space-y-1.5">
            {options.map((label, idx) => {
              const count = parseInt(votes[String(idx)] || 0, 10);
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => cast(idx)}
                  disabled={voted || busy || !expanded}
                  tabIndex={expanded ? 0 : -1}
                  data-testid={`shadow-poll-option-${postId}-${idx}`}
                  className={`relative w-full overflow-hidden rounded-lg border text-left text-[11px] transition-all ${
                    voted
                      ? "border-white/10 bg-white/[0.02] cursor-default"
                      : "border-white/10 bg-white/[0.04] hover:border-cyan-400/40 hover:bg-cyan-400/[0.08] cursor-pointer"
                  }`}
                >
                  {voted && (
                    <div
                      className="absolute inset-y-0 left-0 bg-cyan-400/[0.18] transition-all duration-700"
                      style={{ width: `${pct}%` }}
                      aria-hidden
                    />
                  )}
                  <div className="relative flex items-center justify-between gap-2 px-3 py-1.5">
                    <span className="truncate text-white/85">{label}</span>
                    {voted && (
                      <span className="shrink-0 font-mono text-[10px] font-bold text-cyan-200">
                        {pct}% · {count}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-[10px] text-white/35">
            {total === 0 ? tFases(lang, "poll_first_to_vote") : tFases(lang, total === 1 ? "poll_votes_one" : "poll_votes_other", { n: total })}
            {voted ? tFases(lang, "poll_you_voted") : ""}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AnalysisCard;
